import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { useEffect, useMemo, useRef } from "react"
import { useTranslations } from "use-intl"
import { useTrackError } from "../../providers/TrackErrorProvider"
import { useErrors } from "./useErrors"
import { useSetBlockchainActionErrorsFragment } from "./useSetBlockchainActionErrors.fragment"

type BlockchainActionError = ResultOf<
  typeof useSetBlockchainActionErrorsFragment
>

export const useSetBlockchainActionErrors = (
  errorKeys?: FragmentOf<typeof useSetBlockchainActionErrorsFragment>[],
) => {
  const t = useTranslations("wallet.BlockchainActionErrors")
  const errors = useMemo(() => {
    return errorKeys
      ? errorKeys.map(error =>
          readFragment(useSetBlockchainActionErrorsFragment, error),
        )
      : []
  }, [errorKeys])

  const { setReceiptErrors, setActionError, setInsufficientFundsActionError } =
    useErrors()

  useEffect(
    function setReceiptErrorsFromBlockchainActionResponse() {
      const receiptErrors = errors.reduce<Record<string, string>>(
        (acc, value) => {
          if (value.__typename === "OrderNotFound") {
            const key = value.item.id
            acc[key] = t("orderNotFound")
          }
          return acc
        },
        {},
      )
      setReceiptErrors(receiptErrors)
    },
    [errors, setReceiptErrors, t],
  )

  useEffect(
    function setActionErrorFromBlockchainActionResponse() {
      let actionError: string | undefined
      const errorTypenames = new Set(
        errors
          .filter(error => error.__typename !== "OrderNotFound") // These are handled as receipt errors
          .map(error => error.__typename),
      )
      let insufficientFundsActionError = false
      if (errorTypenames.has("OrderChainMismatchError")) {
        actionError = t("orderChainMismatch")
      } else if (errorTypenames.has("OrderProtocolMismatchError")) {
        actionError = t("orderProtocolMismatch")
      } else if (errorTypenames.has("UnableToFulfillQuantityError")) {
        actionError = t("unableToFulfillQuantity")
      } else if (errorTypenames.has("InsufficientFundError")) {
        actionError = t("insufficientFunds")
        insufficientFundsActionError = true
      } else if (errorTypenames.has("OrderLeverageUserZeroBalanceError")) {
        actionError = t("insufficientOfferBalance")
      } else if (errorTypenames.has("OrderLeverageBalanceRatioError")) {
        actionError = t("insufficientOfferBalanceForExisting")
      } else if (errorTypenames.has("OrderFinalizedError")) {
        actionError = t("noEligibleOrders")
      } else if (errorTypenames.has("EstimateGasFailureError")) {
        actionError = t("orderNoLongerValidRefresh")
      } else if (errorTypenames.has("TransferLockedError")) {
        actionError = t("nftLocked")
      } else if (errorTypenames.has("OrderNotVendableError")) {
        actionError = t("orderNoLongerValid")
      } else if (errorTypenames.has("OrderProviderError")) {
        actionError = t("thirdPartyMarketplaceError")
      } else if (errorTypenames.has("NoTraitItemsFoundError")) {
        actionError = t("noItemsWithTrait")
      } else if (errorTypenames.has("InsufficientMintsRemainingError")) {
        actionError = t("insufficientMintsRemaining")
      } else if (
        errorTypenames.has("MinterNotEligibleForActiveDropStageError")
      ) {
        actionError = t("notEligibleForDropStage")
      } else if (
        errorTypenames.has("MintQuantityMoreThanAllocatedForMinterError")
      ) {
        actionError = t("notEligibleForSelectedQuantity")
      } else if (errorTypenames.has("DropNotFoundError")) {
        actionError = t("dropNotFound")
      } else if (errorTypenames.has("DropNotMintingError")) {
        actionError = t("dropNotMinting")
      } else if (errorTypenames.has("CrossChainPayerNotAllowedForDropError")) {
        actionError = t("crossChainPayerNotAllowedForDrop")
      } else if (errorTypenames.has("OrderPriceChangeError")) {
        actionError = t("orderPriceChange")
      } else if (errorTypenames.has("UnsupportedSwapError")) {
        actionError = t("unsupportedSwap")
      } else if (errorTypenames.size > 0) {
        actionError = t("unexpectedError")
      }
      setActionError(actionError)
      setInsufficientFundsActionError(insufficientFundsActionError)
    },
    [errors, setActionError, t],
  )

  const trackError = useTrackError()
  const reportedErrorsRef = useRef<BlockchainActionError[]>([])
  useEffect(
    function reportErrorsToSentry() {
      if (reportedErrorsRef.current !== errors) {
        errors.forEach(error => {
          switch (error.__typename) {
            case "OrderNotFound":
              trackError(new Error(t("orderNotFound")), {
                extra: { item: error.item.id },
                tags: { chain: error.item.chain.identifier },
              })
              break
            case "OrderChainMismatchError":
            case "OrderProtocolMismatchError":
            case "UnableToFulfillQuantityError":
            case "InsufficientFundError":
            case "OrderLeverageUserZeroBalanceError":
            case "OrderLeverageBalanceRatioError":
            case "OrderFinalizedError":
            case "EstimateGasFailureError":
            case "TransferLockedError":
            case "OrderNotVendableError":
            case "OrderProviderError":
            case "NoTraitItemsFoundError":
            case "UnsupportedSwapError":
              trackError(new Error(error.__typename))
              break
            default:
              trackError(new Error(t("unexpectedBlockchainActionError")), {
                extra: { error: (error as BlockchainActionError).__typename },
              })
              break
          }
        })
        reportedErrorsRef.current = errors
      }
    },
    [errors, trackError, t],
  )
}
